// "Làm đẹp" (v0.6): personal face retouch, applied on the SENDER so everybody —
// the live mirror, the partner's phone, the shared preview and the stored
// full-resolution cut-out — sees exactly the same face (still parity).
//
//   mịn da    edge-preserving (bilateral) smoothing restricted to face skin
//   sáng da   log tone curve on face skin
//   mắt to    local magnification around each iris
//   mặt thon  liquify-style push of the lower cheeks / jaw towards the nose
//   preset    starting values + a skin tone (rosy / milky)
//
// DOM-free: this module turns settings + Face Landmarker points into shader
// parameters (media/beauty-gl.ts draws them). All geometry is in output pixels.

export type BeautyPresetId = 'off' | 'natural' | 'clear' | 'peach' | 'korean' | 'milk';

export interface BeautySettings {
  preset: BeautyPresetId;
  /** 0..100 each */
  smooth: number;
  bright: number;
  eyes: number;
  slim: number;
  /** v0.7 makeup, 0..100: lipstick (preset colour) and cheek blush */
  lip: number;
  blush: number;
}

export interface BeautyPreset extends BeautySettings {
  name: string;
  swatch: string;
  /** skin tone, 0..1 */
  rosy: number;
  milk: number;
  /** lipstick colour, linear-ish RGB 0..1 */
  lipColor: [number, number, number];
}

export const BEAUTY_PRESETS: BeautyPreset[] = [
  { preset: 'off', name: 'Tắt', swatch: '#3A3442', smooth: 0, bright: 0, eyes: 0, slim: 0, lip: 0, blush: 0, rosy: 0, milk: 0, lipColor: [0.8, 0.3, 0.35] },
  { preset: 'natural', name: 'Tự nhiên', swatch: '#E8B79E', smooth: 35, bright: 15, eyes: 8, slim: 10, lip: 20, blush: 15, rosy: 0, milk: 0, lipColor: [0.78, 0.36, 0.38] },
  { preset: 'clear', name: 'Trong veo', swatch: 'linear-gradient(135deg,#FFE9DF,#F5C6B4)', smooth: 50, bright: 30, eyes: 12, slim: 15, lip: 30, blush: 20, rosy: 0.05, milk: 0.1, lipColor: [0.92, 0.42, 0.45] },
  { preset: 'peach', name: 'Hồng đào', swatch: 'linear-gradient(135deg,#FFD6E0,#F4A9B8)', smooth: 45, bright: 25, eyes: 12, slim: 15, lip: 40, blush: 35, rosy: 0.35, milk: 0, lipColor: [0.98, 0.45, 0.5] },
  { preset: 'korean', name: 'Hàn Quốc', swatch: 'linear-gradient(135deg,#FFF1EC,#EFC9C0)', smooth: 60, bright: 35, eyes: 18, slim: 22, lip: 45, blush: 25, rosy: 0.15, milk: 0.15, lipColor: [0.9, 0.22, 0.3] },
  { preset: 'milk', name: 'Sữa', swatch: 'linear-gradient(135deg,#FFFBF5,#EADFD2)', smooth: 55, bright: 45, eyes: 10, slim: 12, lip: 25, blush: 15, rosy: 0, milk: 0.4, lipColor: [0.86, 0.48, 0.48] },
];

export const DEFAULT_BEAUTY: BeautySettings = { preset: 'natural', smooth: 35, bright: 15, eyes: 8, slim: 10, lip: 20, blush: 15 };

export const presetById = (id: string) => BEAUTY_PRESETS.find((p) => p.preset === id) ?? BEAUTY_PRESETS[0];

export type SliderKey = 'smooth' | 'bright' | 'eyes' | 'slim' | 'lip' | 'blush';
export const SLIDERS: { key: SliderKey; label: string }[] = [
  { key: 'smooth', label: 'Mịn da' },
  { key: 'bright', label: 'Sáng da' },
  { key: 'eyes', label: 'Mắt to' },
  { key: 'slim', label: 'Mặt thon' },
  { key: 'lip', label: 'Son môi' },
  { key: 'blush', label: 'Má hồng' },
];

const clamp100 = (v: unknown, d: number) => (typeof v === 'number' && Number.isFinite(v) ? Math.max(0, Math.min(100, Math.round(v))) : d);

/** Validates stored / user input. */
export function normalizeBeauty(v: unknown): BeautySettings {
  const o = (v && typeof v === 'object' ? v : {}) as Partial<BeautySettings>;
  const p = BEAUTY_PRESETS.some((x) => x.preset === o.preset) ? o.preset! : DEFAULT_BEAUTY.preset;
  return {
    preset: p,
    smooth: clamp100(o.smooth, DEFAULT_BEAUTY.smooth),
    bright: clamp100(o.bright, DEFAULT_BEAUTY.bright),
    eyes: clamp100(o.eyes, DEFAULT_BEAUTY.eyes),
    slim: clamp100(o.slim, DEFAULT_BEAUTY.slim),
    lip: clamp100(o.lip, DEFAULT_BEAUTY.lip),
    blush: clamp100(o.blush, DEFAULT_BEAUTY.blush),
  };
}

export const beautyActive = (s: BeautySettings) =>
  s.smooth > 0 || s.bright > 0 || s.eyes > 0 || s.slim > 0 || s.lip > 0 || s.blush > 0
  || presetById(s.preset).rosy > 0 || presetById(s.preset).milk > 0;

/** Face Landmarker (478-point mesh) indices used here. */
export const LM = {
  irisA: 468, irisB: 473,
  eyeA: [33, 133] as const, eyeB: [362, 263] as const,
  cheekL: 234, cheekR: 454,
  forehead: 10, chin: 152, nose: 1,
  mouthL: 61, mouthR: 291, lipTop: 13, lipBottom: 14,
  /** lower cheek + jaw angle on both sides (pushed inwards by "mặt thon") */
  slim: [132, 172, 361, 397] as const,
};
export const LM_MIN = 478;

/** Shader parameters for one frame (pixel space of the output image). */
export interface BeautyParams {
  /** face ellipse: cx, cy, rx, ry — the only region that is retouched */
  face: [number, number, number, number];
  /** eye magnification: per eye cx, cy, radius; strength 0..~0.22 */
  eyes: [number, number, number][];
  eyeK: number;
  /** liquify points: cx, cy, radius, dx, dy */
  slim: [number, number, number, number, number][];
  /** protected ellipses (eyes, mouth): cx, cy, rx, ry */
  protect: [number, number, number, number][];
  smooth: number;
  /** bilateral radius in px (scales with the face) */
  radius: number;
  bright: number;
  rosy: number;
  milk: number;
  /** lipstick strength 0..1 and colour; the lip mask itself is drawn from the mesh */
  lip: number;
  lipColor: [number, number, number];
  /** blush strength 0..1 and spots (cx, cy, r px) */
  blush: number;
  blushSpots: [number, number, number][];
}

/**
 * Landmarks are normalised (x, y) pairs over the image the face was detected in;
 * `w × h` is the output image (live processing size or full camera resolution —
 * the same normalised points serve both, so the still matches the live view).
 */
export function beautyParams(
  s: BeautySettings, lm: Float32Array | null, w: number, h: number,
  /** v0.8: 0..1 how fast the face moves — the geometric warps fade out while the mesh may lag behind */
  motion = 0,
): BeautyParams | null {
  if (!lm || lm.length < LM_MIN * 2 || !beautyActive(s)) return null;
  const P = (i: number): [number, number] => [lm[i * 2] * w, lm[i * 2 + 1] * h];
  const dist = (a: [number, number], b: [number, number]) => Math.hypot(a[0] - b[0], a[1] - b[1]);
  const mid = (a: [number, number], b: [number, number]): [number, number] => [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];

  const faceW = dist(P(LM.cheekL), P(LM.cheekR));
  const faceH = dist(P(LM.forehead), P(LM.chin));
  if (!(faceW > 8 && faceH > 8)) return null;
  const pre = presetById(s.preset);
  const fc = mid(P(LM.forehead), P(LM.chin));
  const nose = P(LM.nose);
  // v0.8: a turned head foreshortens one side: its jaw points end up at the edge of the face,
  // next to the ear and hair, and a warp there drags hair / room into the cheek (dark patch).
  // Each side's effects fade with how much of that side is still visible.
  const dl = dist(nose, P(LM.cheekL)), dr = dist(nose, P(LM.cheekR));
  const share = (d: number) => Math.max(0, Math.min(1, (d / (dl + dr || 1) - 0.25) / 0.2));
  const sideL = share(dl), sideR = share(dr);
  const sideOf = (p: [number, number]) => (dist(p, P(LM.cheekL)) < dist(p, P(LM.cheekR)) ? sideL : sideR);
  const still = 1 - Math.max(0, Math.min(1, motion));

  const eyes: [number, number, number][] = [];
  const protect: [number, number, number, number][] = [];
  for (const [iris, [a, b]] of [[LM.irisA, LM.eyeA], [LM.irisB, LM.eyeB]] as const) {
    const ew = dist(P(a), P(b));
    const c = P(iris);
    eyes.push([c[0], c[1], ew * 0.95]);
    const m = mid(P(a), P(b));
    protect.push([m[0], m[1] - ew * 0.15, ew * 0.8, ew * 0.6]);
  }
  const mw = dist(P(LM.mouthL), P(LM.mouthR));
  const mc = mid(P(LM.lipTop), P(LM.lipBottom));
  protect.push([mc[0], mc[1], mw * 0.62, dist(P(LM.lipTop), P(LM.lipBottom)) / 2 + mw * 0.28]);

  // the two points of each side overlap, so the jaw moves up to ~6% of the face width
  const push = (s.slim / 100) * 0.035 * faceW * still;
  const slim: [number, number, number, number, number][] = s.slim > 0 && push > 0.05 ? LM.slim.map((i) => {
    const p = P(i);
    const dx = nose[0] - p[0], dy = nose[1] - p[1];
    const d = Math.hypot(dx, dy) || 1;
    const k = push * sideOf(p);
    // smaller reach (was 0.42): the warp stays on the jaw and does not grab the ear / hair
    return [p[0], p[1], faceW * 0.3, (dx / d) * k, (dy / d) * k * 0.35] as [number, number, number, number, number];
  }).filter((x) => Math.hypot(x[3], x[4]) > 0.05) : [];

  return {
    face: [fc[0], fc[1], faceW * 0.62, faceH * 0.66],
    eyes: s.eyes > 0 && still > 0.05 ? eyes : [],
    eyeK: (s.eyes / 100) * 0.22 * still,
    slim,
    protect,
    smooth: s.smooth / 100,
    radius: Math.max(2, faceW * 0.045),
    bright: s.bright / 100,
    rosy: pre.rosy,
    milk: pre.milk,
    lip: s.lip / 100,
    lipColor: pre.lipColor,
    blush: s.blush / 100,
    // a turned-away cheek gets a smaller spot (it would otherwise tint the ear / hair)
    blushSpots: [50, 280].map((i) => { const p = P(i); return [p[0], p[1], Math.max(1, faceW * 0.19 * sideOf(p))] as [number, number, number]; }),
  };
}

/**
 * CPU reference of the geometric warp (the shader does exactly this): where
 * output pixel `p` samples the source. Used by the tests.
 */
export function warpSource(p: [number, number], b: BeautyParams): [number, number] {
  let [x, y] = p;
  for (const [cx, cy, r, dx, dy] of b.slim) {
    const q = ((x - cx) ** 2 + (y - cy) ** 2) / (r * r);
    if (q < 1) { const f = (1 - q) * (1 - q); x -= dx * f; y -= dy * f; }
  }
  for (const [cx, cy, r] of b.eyes) {
    const q = ((x - cx) ** 2 + (y - cy) ** 2) / (r * r);
    if (q < 1) { const k = 1 - b.eyeK * (1 - q); x = cx + (x - cx) * k; y = cy + (y - cy) * k; }
  }
  return [x, y];
}

const KEY = 'booth.beauty.v2';

export function loadBeauty(): BeautySettings {
  try { return normalizeBeauty(JSON.parse(localStorage.getItem(KEY) ?? 'null')); } catch { return { ...DEFAULT_BEAUTY }; }
}

export function saveBeauty(s: BeautySettings) {
  try { localStorage.setItem(KEY, JSON.stringify(s)); } catch { /* private mode: per-session only */ }
}

// ------------------------------------------------------------------ face sticker (v0.7)

const STICKER_KEY = 'booth.sticker.v1';

export function loadSticker(): string {
  try { return localStorage.getItem(STICKER_KEY) ?? 'none'; } catch { return 'none'; }
}

export function saveSticker(id: string) {
  try { localStorage.setItem(STICKER_KEY, id); } catch { /* per session only */ }
}
